import { router } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  ConfirmLocationActionSheet,
  ConfirmStoreActionSheet,
} from '@fhs/routing/src/components/home-reorder-confirmation';
import { OrderDisplayType } from '@fhs/ui/src/components/order-display/types';
import { IHistoricalOrder } from '@rbi-ctg/menu';
import { getServiceModeBottomCTAText } from 'pages/cart/types';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import logger from 'utils/logger';
import { routes } from 'utils/routing';
import { isDelivery as _isDelivery } from 'utils/service-mode';

import {
  REORDER_DIFFERENT_SERVICE_MODE_LOG,
  REORDER_PROCEEDING_WITH_ANOTHER_ADDRESS_LOG,
  REORDER_PROCEEDING_WITH_ANOTHER_STORE_LOG,
  REORDER_PROCEEDING_WITH_RECENT_STORE_LOG,
  REORDER_PROCEEDING_WITH_SELECTED_STORE_LOG,
} from '../constants/reorder-logs';

import { useHomepageReorderEnabled } from './use-homepage-reorder-enabled';
import { useUserRecentOrder } from './use-user-recent-order';

export const useHomepageReorder = (singleOrder?: IHistoricalOrder) => {
  const isHomepageReorderEnabled = useHomepageReorderEnabled();

  const [showReorderStoreConfirmationActionsheet, setShowReorderStoreConfirmationActionsheet] =
    useState(false);

  const [
    showReorderLocationConfirmationActionsheet,
    setShowReorderLocationConfirmationActionsheet,
  ] = useState(false);

  const { serviceMode: selectedServiceMode, setServiceMode } = useServiceModeContext();
  const { formatMessage } = useIntl();
  const {
    store,
    recentStoreNumber,
    recentOrder,
    handlePickupReorder,
    handleDeliveryReorder,
    selectDeliveryAddress,
    handleStoreSelectAndReorder,
    recentOrderLoading,
    isReorderingLoading,
  } = useUserRecentOrder({ singleOrder }); // We can specify order if we already have it.

  const displayedOrder = useMemo(() => {
    if (isHomepageReorderEnabled) {
      return recentOrder;
    } else if (
      !isHomepageReorderEnabled &&
      recentOrder?.type === OrderDisplayType.ORDER_IN_PROGRESS
    ) {
      // If homepage reorder is disabled, we only return the recent order if it's in progress
      return recentOrder;
    }

    return null;
  }, [isHomepageReorderEnabled, recentOrder]);

  const handleReorderCallback = useCallback(() => {
    if (!displayedOrder || !displayedOrder.serviceMode || !isHomepageReorderEnabled) {
      return;
    }
    // Displayed order has a different service mode from the selected service mode
    if (selectedServiceMode && displayedOrder.serviceMode !== selectedServiceMode) {
      logger.info({
        message: REORDER_DIFFERENT_SERVICE_MODE_LOG,
        context: {
          displayedOrder,
          serviceMode: displayedOrder.serviceMode,
        },
      });

      setShowReorderLocationConfirmationActionsheet(true);
      return;
    }

    // Displayed order is a Delivery order
    if (_isDelivery(displayedOrder.serviceMode as ServiceMode)) {
      // Delivery scenarios
      handleDeliveryReorder(() => setShowReorderStoreConfirmationActionsheet(true));
      return;
    }

    // Displayed order is a Pickup order
    handlePickupReorder(() => setShowReorderStoreConfirmationActionsheet(true));
  }, [
    isHomepageReorderEnabled,
    selectedServiceMode,
    displayedOrder,
    handlePickupReorder,
    handleDeliveryReorder,
    setShowReorderStoreConfirmationActionsheet,
    setShowReorderLocationConfirmationActionsheet,
  ]);

  const locationConfirmDialogOptions = useMemo(() => {
    if (!displayedOrder || !recentStoreNumber || !displayedOrder.serviceMode) {
      return null;
    }

    const currentServiceModeFormatted = selectedServiceMode
      ? getServiceModeBottomCTAText(formatMessage)[selectedServiceMode]
      : '';

    const recentOrderIsDelivery = _isDelivery(displayedOrder.serviceMode as ServiceMode);
    const currentSelectModeIsDelivery = _isDelivery(selectedServiceMode);

    return {
      option1: {
        isDelivery: recentOrderIsDelivery,
        storeName: recentOrderIsDelivery
          ? displayedOrder.storeAddress
          : displayedOrder.storeName ?? '',
        storeAddress: `${displayedOrder.serviceModeTitle} • ${displayedOrder.storeAddress}`,
        onPress: () => {
          setShowReorderLocationConfirmationActionsheet(false);

          logger.info({
            message: REORDER_PROCEEDING_WITH_RECENT_STORE_LOG,
            context: {
              displayedOrder,
              serviceMode: displayedOrder.serviceMode,
            },
          });

          setServiceMode(displayedOrder.serviceMode as ServiceMode);

          if (recentOrderIsDelivery) {
            selectDeliveryAddress({
              address: displayedOrder.storeName ?? '',
              orderId: displayedOrder.id,
            });

            return;
          }

          handleStoreSelectAndReorder(recentStoreNumber.toString());
        },
      },
      option2: {
        isDelivery: currentSelectModeIsDelivery,
        storeName:
          (currentSelectModeIsDelivery ? store.physicalAddress?.address1 : store.name) ?? '',
        storeAddress: `${currentServiceModeFormatted}. • ${store.physicalAddress?.address1}, ${store.physicalAddress?.stateProvince}`,
        onPress: () => {
          setShowReorderLocationConfirmationActionsheet(false);

          logger.info({
            message: REORDER_PROCEEDING_WITH_SELECTED_STORE_LOG,
            context: {
              displayedOrder,
              store,
              serviceMode: displayedOrder.serviceMode,
            },
          });

          if (currentSelectModeIsDelivery) {
            selectDeliveryAddress({
              address: store?.physicalAddress?.address1,
              orderId: displayedOrder.id,
            });

            return;
          }

          handleStoreSelectAndReorder(store?.number ?? '');
        },
      },
      option3: {
        label: formatMessage({ id: 'selectDifferentPickupStore' }),
        onPress: () => {
          setShowReorderLocationConfirmationActionsheet(false);

          logger.info({
            message: REORDER_PROCEEDING_WITH_ANOTHER_STORE_LOG,
            context: {
              displayedOrder,
              store,
              serviceMode: displayedOrder.serviceMode,
            },
          });

          router.navigate({
            pathname: routes.serviceMode,
            params: { reorderOrderId: displayedOrder.id },
          });
        },
      },
      option4: {
        label: formatMessage({ id: 'selectDifferentDeliveryAddress' }),
        onPress: () => {
          setShowReorderLocationConfirmationActionsheet(false);

          logger.info({
            message: REORDER_PROCEEDING_WITH_ANOTHER_ADDRESS_LOG,
            context: {
              displayedOrder,
              store,
              serviceMode: displayedOrder.serviceMode,
            },
          });

          selectDeliveryAddress({
            address: '',
            orderId: displayedOrder.id,
          });
        },
      },
    };
  }, [
    displayedOrder,
    recentStoreNumber,
    handleStoreSelectAndReorder,
    formatMessage,
    selectedServiceMode,
    setServiceMode,
    selectDeliveryAddress,
    store,
  ]);

  const storeSelectDialogOptions = useMemo(() => {
    if (!displayedOrder || !recentStoreNumber || !displayedOrder.serviceMode) {
      return null;
    }

    const currentServiceModeFormatted = selectedServiceMode
      ? getServiceModeBottomCTAText(formatMessage)[selectedServiceMode]
      : '';

    if (_isDelivery(displayedOrder.serviceMode as ServiceMode)) {
      return {
        isDelivery: true,
        option1: {
          storeName: displayedOrder.storeAddress ?? '',
          storeAddress: `${displayedOrder.serviceModeTitle} • ${displayedOrder.storeAddress}`,
          onPress: () => {
            setShowReorderStoreConfirmationActionsheet(false);

            logger.info({
              message: REORDER_PROCEEDING_WITH_RECENT_STORE_LOG,
              context: {
                displayedOrder,
                serviceMode: displayedOrder.serviceMode,
              },
            });

            selectDeliveryAddress({
              address: displayedOrder.storeName ?? '',
              orderId: displayedOrder.id,
            });
          },
        },
        option2: {
          storeName: store.physicalAddress?.address1 ?? '',
          storeAddress: `${currentServiceModeFormatted}. • ${store.physicalAddress?.address1}, ${store.physicalAddress?.stateProvince}`,
          onPress: () => {
            setShowReorderStoreConfirmationActionsheet(false);

            logger.info({
              message: REORDER_PROCEEDING_WITH_SELECTED_STORE_LOG,
              context: {
                displayedOrder,
                store,
                serviceMode: displayedOrder.serviceMode,
              },
            });

            selectDeliveryAddress({
              address: store?.physicalAddress?.address1,
              orderId: displayedOrder.id,
            });
          },
        },
        option3: {
          label: formatMessage({ id: 'selectDifferentAddress' }),
          onPress: () => {
            setShowReorderStoreConfirmationActionsheet(false);

            logger.info({
              message: REORDER_PROCEEDING_WITH_ANOTHER_ADDRESS_LOG,
              context: {
                displayedOrder,
                store,
                serviceMode: displayedOrder.serviceMode,
              },
            });

            selectDeliveryAddress({
              address: '',
              orderId: displayedOrder.id,
            });
          },
        },
      };
    }

    return {
      isDelivery: false,
      option1: {
        storeName: displayedOrder.storeName ?? '',
        storeAddress: `${displayedOrder.serviceModeTitle} • ${displayedOrder.storeAddress}`,
        onPress: () => {
          setShowReorderStoreConfirmationActionsheet(false);

          logger.info({
            message: REORDER_PROCEEDING_WITH_RECENT_STORE_LOG,
            context: {
              displayedOrder,
              serviceMode: displayedOrder.serviceMode,
            },
          });

          handleStoreSelectAndReorder(recentStoreNumber.toString());
        },
      },
      option2: {
        storeName: store.name ?? '',
        storeAddress: `${currentServiceModeFormatted}. • ${store.physicalAddress?.address1}, ${store.physicalAddress?.stateProvince}`,
        onPress: () => {
          setShowReorderStoreConfirmationActionsheet(false);

          logger.info({
            message: REORDER_PROCEEDING_WITH_SELECTED_STORE_LOG,
            context: {
              displayedOrder,
              store,
              serviceMode: displayedOrder.serviceMode,
            },
          });

          handleStoreSelectAndReorder(store?.number ?? '');
        },
      },
      option3: {
        label: formatMessage({ id: 'selectDifferentStore' }),
        onPress: () => {
          setShowReorderStoreConfirmationActionsheet(false);

          logger.info({
            message: REORDER_PROCEEDING_WITH_ANOTHER_STORE_LOG,
            context: {
              displayedOrder,
              store,
              serviceMode: displayedOrder.serviceMode,
            },
          });

          router.navigate({
            pathname: routes.serviceMode,
            params: { reorderOrderId: displayedOrder.id },
          });
        },
      },
    };
  }, [
    displayedOrder,
    recentStoreNumber,
    handleStoreSelectAndReorder,
    formatMessage,
    selectedServiceMode,
    selectDeliveryAddress,
    store,
  ]);

  const ConfirmLocationActionSheetComponent = () =>
    locationConfirmDialogOptions && (
      <ConfirmLocationActionSheet
        isVisible={
          showReorderLocationConfirmationActionsheet && !showReorderStoreConfirmationActionsheet
        }
        option1={locationConfirmDialogOptions.option1}
        option2={locationConfirmDialogOptions.option2}
        option3={locationConfirmDialogOptions.option3}
        option4={locationConfirmDialogOptions.option4}
        onClose={() => setShowReorderLocationConfirmationActionsheet(false)}
      />
    );

  const ConfirmStoreActionSheetComponent = () =>
    storeSelectDialogOptions && (
      <ConfirmStoreActionSheet
        isVisible={
          showReorderStoreConfirmationActionsheet && !showReorderLocationConfirmationActionsheet
        }
        isDelivery={storeSelectDialogOptions.isDelivery}
        option1={storeSelectDialogOptions.option1}
        option2={storeSelectDialogOptions.option2}
        option3={storeSelectDialogOptions.option3}
        onClose={() => setShowReorderStoreConfirmationActionsheet(false)}
      />
    );

  return {
    displayedOrder,
    recentOrderLoading,
    isReorderingLoading,
    handleReorderCallback,
    ConfirmLocationActionSheetComponent,
    ConfirmStoreActionSheetComponent,
  };
};
